import { Delete } from "lucide-react"
import { cn } from "@/lib/format"

export type TastenModus = "zahl" | "minsek" | "ganz"

interface Props {
  modus: TastenModus
  onTaste: (taste: string) => void
  onWeiter: () => void
  weiterText?: string
}

/**
 * Großes Zahlenfeld für Touch und Handschuhe. "," bei Dezimalzahlen, ":" bei min:sec;
 * „Weiter“ springt ins nächste Feld. Physische Tastaturen funktionieren zusätzlich.
 */
export function Tastenfeld({ modus, onTaste, onWeiter, weiterText = "Weiter" }: Props) {
  const sonder = modus === "minsek" ? ":" : modus === "zahl" ? "," : ""
  const tasten = ["7", "8", "9", "4", "5", "6", "1", "2", "3", sonder, "0", "⌫"]
  return (
    <div className="grid select-none grid-cols-3 gap-2" role="group" aria-label="Zahlenfeld">
      {tasten.map((t, i) => (
        <button
          key={i}
          type="button"
          disabled={!t}
          onPointerDown={(e) => {
            e.preventDefault() // Fokus im Eingabefeld behalten
            if (t) onTaste(t)
          }}
          className={cn(
            "flex h-16 items-center justify-center rounded-xl border border-hair/10 bg-surface text-2xl font-semibold tabular active:scale-[0.97] active:bg-surface2 disabled:opacity-0",
            t === "⌫" && "text-ink2",
          )}
          aria-label={t === "⌫" ? "Löschen" : t}
        >
          {t === "⌫" ? <Delete className="h-7 w-7" aria-hidden="true" /> : t}
        </button>
      ))}
      <button
        type="button"
        onPointerDown={(e) => {
          e.preventDefault()
          onWeiter()
        }}
        className="col-span-3 h-16 rounded-xl bg-accent-strong text-xl font-semibold text-white active:scale-[0.99]"
      >
        {weiterText}
      </button>
    </div>
  )
}

/** Wendet eine Taste auf den Feldinhalt an (mit Formatregeln je Modus). */
export function tasteAnwenden(wert: string, taste: string, modus: TastenModus): string {
  if (taste === "⌫") return wert.slice(0, -1)
  if (modus === "minsek") {
    if (taste === ":") return wert.includes(":") || !wert ? wert : `${wert}:`
    const neu = wert + taste
    // Ohne Doppelpunkt nach 3 Ziffern automatisch setzen: "814" → "8:14"
    if (!neu.includes(":") && neu.length === 3) return `${neu[0]}:${neu.slice(1)}`
    if (neu.includes(":") && neu.split(":")[1].length > 2) return wert
    return neu.length > 5 ? wert : neu
  }
  if (taste === ",") return modus === "zahl" && !wert.includes(",") ? `${wert || "0"},` : wert
  if (modus === "zahl" && wert.includes(",") && wert.split(",")[1].length >= 2) return wert
  return wert.length >= 7 ? wert : wert + taste
}
